import React, { useCallback, useEffect, useRef, useState } from 'react';
import { candidateAPI } from '../services/api';
import { formatDateTime } from '../utils/format';
import './TrainingLogModal.css';

const COLUMNS = [
  { key: 'observed', label: 'Observed' },
  { key: 'supervised', label: 'Supervised' },
  { key: 'independently', label: 'Independently' },
];

// Ticks are plain ISO dates (the practice's day, stamped by the server).
const formatTickDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

// A task cell written over several lines in the template (the Protocols row):
// the first line is its title, the rest a bullet list. Dashes the template
// used as bullets, and a trailing "-" or ":" on the title, are dropped.
const TaskLabel = ({ label }) => {
  const lines = (label || '').split('\n').map((line) => line.trim()).filter(Boolean);
  if (lines.length < 2) return label;
  const [title, ...items] = lines;
  return (
    <>
      {title.replace(/\s*[-:]$/, '')}
      <ul className="task-label-list">
        {items.map((item, i) => <li key={i}>{item.replace(/^-\s*/, '')}</li>)}
      </ul>
    </>
  );
};

/**
 * Part 6 Training Log: the position's task table, ticked off in the app while
 * the new starter is trained, then sent for their eSignature. Each tick is one
 * click (stamps today's date; a second click clears it) and saves immediately.
 * "Request signature" may be pressed with rows still blank — the confirmation
 * says how many — and again later; each request supersedes the last.
 */
function TrainingLogModal({
  isOpen,
  candidateId,
  candidateName,
  candidateEmail,
  usingSignWell,
  driveDirect,
  onClose,
  onChanged,                // the parent reloads Part 6 after a tick or a request
  showConfirm,
  showAlert,
  openCandidateDocument,
  onCopySigningLink,        // SignWell: copy the open request's signing link
}) {
  const [log, setLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingCell, setPendingCell] = useState(null);   // "taskId:column" being saved
  const [requesting, setRequesting] = useState(false);
  const changed = useRef(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setLog(await candidateAPI.getTrainingLog(candidateId));
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => {
    if (isOpen) {
      changed.current = false;
      load();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, load]);

  const close = () => {
    if (changed.current && onChanged) onChanged();
    onClose();
  };

  const status = log?.status || {};
  const signed = Boolean(status.signed_date);
  const requested = Boolean(status.signature_requested_date);

  const toggle = async (task, column) => {
    if (signed || pendingCell) return;
    const key = `${task.id}:${column}`;
    const ticked = !(log.ticks?.[task.id]?.[column]);
    try {
      setPendingCell(key);
      const updated = await candidateAPI.tickTrainingLog(candidateId, task.id, column, ticked);
      setLog(updated);
      changed.current = true;
    } catch (err) {
      showAlert(`Could not save that tick: ${err.response?.data?.detail || err.message}`, 'error');
    } finally {
      setPendingCell(null);
    }
  };

  const requestSignature = async (event) => {
    if (!log || requesting) return;
    const { ticked_rows: ticked, total_rows: total } = log.progress;
    const blank = total - ticked;
    const lines = [
      `Send the Training Log to ${candidateName} (${candidateEmail}) for signature?`,
      '',
      `${ticked} of ${total} tasks ticked` + (blank > 0 ? ` — ${blank} still blank.` : '.'),
      '',
      usingSignWell
        ? 'The ticks are written into the document and it is sent as a SignWell request. The new starter signs it from the email.'
        : 'The ticks are written into the document, which opens in a new tab for you to request the eSignature in Google Docs.',
    ];
    if (requested && !signed) lines.push('', 'This replaces the request already raised.');

    // Google flow: the tab must be opened inside the click — after the awaits
    // below it would be treated as a pop-up and blocked.
    const tab = usingSignWell ? null : window.open('', '_blank');
    if (tab) tab.document.write('<p style="font-family: sans-serif; padding: 24px;">Preparing the Training Log…</p>');

    const ok = await showConfirm(lines.join('\n'), event);
    if (!ok) {
      if (tab) tab.close();
      return;
    }

    try {
      setRequesting(true);
      const result = await candidateAPI.requestTrainingLogSignature(candidateId);
      changed.current = true;
      await load();
      if (tab) {
        if (result.open_url) tab.location.href = result.open_url;
        else tab.close();
      }
      if (result.esign_test_mode) {
        showAlert('SignWell request created in TEST MODE — no email was sent. Use "Copy signing link" to open it.', 'info');
      } else if (usingSignWell) {
        showAlert(`✓ Training Log sent to ${candidateEmail} for signature via SignWell.`, 'success');
      } else {
        showAlert(
          'The filled Training Log has opened in a new tab. Request the eSignature there (File → eSignature), ' +
          'then press "Check requests sent" in Part 6 to confirm it reached the new starter.',
          'info'
        );
      }
    } catch (err) {
      if (tab) tab.close();
      showAlert(`Could not request the signature: ${err.response?.data?.detail || err.message}`, 'error');
    } finally {
      setRequesting(false);
    }
  };

  if (!isOpen) return null;

  const progress = log?.progress || { ticked_rows: 0, total_rows: 0, independent_rows: 0 };

  let statusText;
  if (signed) {
    statusText = <><strong>Signed</strong> {formatDateTime(status.signed_date)}</>;
  } else if (requested) {
    statusText = (
      <>
        <strong>Signature requested</strong> {formatDateTime(status.signature_requested_date)}
        {status.esign_document_id
          ? ` · via SignWell (${status.esign_status || 'sent'})`
          : status.request_state === 'sent'
            ? ' · request confirmed sent'
            : status.request_state === 'misdirected'
              ? ' · ⚠ sent to the wrong recipient'
              : ' · eSignature request not yet raised in Google Docs'}
        {' '}— ticks made since are not in that copy; request again to send them.
      </>
    );
  } else {
    statusText = <><strong>Not yet sent for signature.</strong> Tick tasks as training progresses; request the signature when ready.</>;
  }

  return (
    <>
      <div className="training-log-overlay" onClick={close} />
      <div className="training-log-modal" role="dialog" aria-modal="true" aria-labelledby="training-log-title">
        <div className="training-log-header">
          <div>
            <h2 id="training-log-title">{log?.label || 'Training Log'}</h2>
            <p className="training-log-subtitle">
              {candidateName}
              {log && (
                <>
                  {' · '}
                  <span className="training-log-progress">
                    {progress.ticked_rows} of {progress.total_rows} tasks ticked
                  </span>
                  {progress.independent_rows > 0 && ` · ${progress.independent_rows} independent`}
                  {log.updated_date && ` · last tick ${formatDateTime(log.updated_date)}`}
                </>
              )}
            </p>
          </div>
          <button type="button" className="modal-close" onClick={close} aria-label="Close">×</button>
        </div>

        <div className="training-log-body">
          {loading && !log ? (
            <p className="training-log-loading">Loading the Training Log…</p>
          ) : error ? (
            <p className="training-log-error">{error}</p>
          ) : log ? (
            <table className="training-log-table">
              <thead>
                <tr>
                  <th>Task</th>
                  {COLUMNS.map((col) => <th key={col.key} className="tick-col">{col.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {log.tasks.map((task) => (
                  task.heading ? (
                    <tr key={task.id} className="is-heading">
                      <td colSpan={COLUMNS.length + 1}>{task.label}</td>
                    </tr>
                  ) : (
                    <tr key={task.id}>
                      <td><TaskLabel label={task.label} /></td>
                      {COLUMNS.map((col) => {
                        const note = task.notes?.[col.key];
                        if (note) return <td key={col.key} className="note-cell">{note}</td>;
                        const date = log.ticks?.[task.id]?.[col.key];
                        const saving = pendingCell === `${task.id}:${col.key}`;
                        return (
                          <td key={col.key} className="tick-cell">
                            <button
                              type="button"
                              className={`tick-button${date ? ' is-ticked' : ''}`}
                              onClick={() => toggle(task, col.key)}
                              disabled={signed || Boolean(pendingCell)}
                              title={signed
                                ? 'The log has been signed'
                                : date ? `Ticked ${formatTickDate(date)} — click to clear` : `Tick ${col.label.toLowerCase()} today`}
                            >
                              {saving ? '…' : date ? `✓ ${formatTickDate(date)}` : '○'}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          ) : null}
        </div>

        <div className="training-log-footer">
          <div className={`training-log-status${signed ? ' is-signed' : ''}`}>{log && statusText}</div>
          <div className="training-log-actions">
            {/* Google only: the Doc is where the manual request is raised. Under
                SignWell the table above is the log, and the Doc is rebuilt on every
                request, so edits made in it would be lost. */}
            {status.pdf_url && !usingSignWell && (
              <button
                type="button"
                className="btn-small btn-blue"
                onClick={() => openCandidateDocument('induction', status.pdf_url, log.doc_key)}
              >
                View document
              </button>
            )}
            {status.signed_pdf_url && (
              <a
                href={status.signed_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-small btn-success"
                style={{ textDecoration: 'none' }}
                onClick={(e) => {
                  if (driveDirect) return;
                  e.preventDefault();
                  openCandidateDocument('induction_signed', status.signed_pdf_url, log.doc_key);
                }}
              >
                Signed PDF
              </a>
            )}
            {usingSignWell && status.esign_document_id && !signed && (
              <button type="button" className="btn-small btn-success" onClick={() => onCopySigningLink('training_log')}>
                Copy signing link
              </button>
            )}
            {log && !signed && (
              <button
                type="button"
                className="btn-pink"
                style={{ fontWeight: 600 }}
                onClick={requestSignature}
                disabled={requesting || loading}
              >
                {requesting ? 'Preparing…' : requested ? 'Request signature again' : 'Request signature'}
              </button>
            )}
            <button type="button" className="btn-secondary" onClick={close}>Close</button>
          </div>
        </div>
      </div>
    </>
  );
}

export default TrainingLogModal;
